import React, { ReactNode, useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";

// Shared type for children
interface ChildrenProps {
    children: ReactNode;
    className?: string;
}

// Navigation Menu Components
const NavigationMenu: React.FC<ChildrenProps> = ({ children, className }) => (
    <nav className={`flex items-center justify-center ${className || ''}`}>
        {children}
    </nav>
);

const NavigationMenuList: React.FC<ChildrenProps> = ({ children, className }) => (
    <ul className={`flex items-center justify-center space-x-1 list-none ${className || ''}`}>
        {children}
    </ul>
);

interface NavigationMenuItemProps extends ChildrenProps {
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
}

const NavigationMenuItem: React.FC<NavigationMenuItemProps> = ({
    children,
    onMouseEnter,
    onMouseLeave,
    className,
}) => (
    <li onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} className={className}>
        {children}
    </li>
);

interface NavigationMenuTriggerProps extends ChildrenProps {
    isActive?: boolean;
}

const NavigationMenuTrigger: React.FC<NavigationMenuTriggerProps> = ({
    children,
    isActive = false,
    className,
}) => (
    <button
        className={`inline-flex h-10 items-center justify-center px-4 py-2 text-xs transition-colors focus:outline-none ${isActive ? "text-background" : "text-background/90 hover:text-background"
            } ${className || ''}`}
    >
        {children}
    </button>
);

interface NavigationMenuLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
    children: ReactNode;
    className?: string;
    isActive?: boolean;
    hasChildren?: boolean;
}

const NavigationMenuLink = React.forwardRef<HTMLAnchorElement, NavigationMenuLinkProps>(({
    children,
    isActive = false,
    hasChildren = false,
    className,
    ...props
}, ref) => (
    <a
        ref={ref}
        className={`inline-flex h-10 items-center justify-center px-4 py-2 text-xs font-medium transition-colors cursor-pointer group pointer-events-auto ${isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
            } ${className || ''}`}
        {...props}
    >
        {children}
        {hasChildren && (
            <ChevronDown
                className={`ml-1 h-3 w-3 transition-transform duration-200 group-hover:rotate-180 ${isActive ? 'rotate-180' : ''}`}
            />
        )}
    </a>
));
NavigationMenuLink.displayName = "NavigationMenuLink";

interface NavigationMenuContentProps extends ChildrenProps {
    isOpen?: boolean;
    variant?: 'full' | 'standard'; // 'full' = Mega Menu (Screen width), 'standard' = Dropdown (Parent relative)
}

const NavigationMenuContent: React.FC<NavigationMenuContentProps> = ({
    children,
    isOpen = false,
    variant = 'full', // Default to full for backward compat with current state
    className,
}) => {
    const [visible, setVisible] = useState(isOpen);

    useEffect(() => {
        if (isOpen) {
            setVisible(true); // mount immediately
        } else {
            // delay unmount until after fade-out
            const timeout = setTimeout(() => setVisible(false), 400);
            return () => clearTimeout(timeout);
        }
    }, [isOpen]);

    if (!visible) return null;

    // Styles for 'full' (Mega Menu)
    // Positioned absolute to the nearest relative parent (usually the Nav container).
    // To ensure full width, the Nav container should be full width.
    const fullStyles = `absolute start-0 top-full w-full bg-primary text-primary-foreground border-t border-primary-foreground/10 shadow-lg`;

    // Styles for 'standard' (Dropdown List)
    // Needs to be positioned relative to the parent MenuItem. 
    // Note: Parent MenuItem needs 'relative' class.
    const standardStyles = `absolute top-full start-0 min-w-[220px] bg-background text-foreground border border-border shadow-md rounded-md mt-1`;

    const baseStyles = variant === 'full' ? fullStyles : standardStyles;

    return (
        <div
            className={`${baseStyles} z-50 transform transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]
        ${isOpen ? "opacity-100 translate-y-0 visible" : "opacity-0 -translate-y-2 invisible pointer-events-none"}
        ${className || ''}
      `}
        >
            {variant === 'full' ? (
                <div className="container mx-auto px-4 py-8">{children}</div>
            ) : (
                <div className="p-2">{children}</div>
            )}
        </div>
    );
};

export {
    NavigationMenu,
    NavigationMenuList,
    NavigationMenuItem,
    NavigationMenuTrigger,
    NavigationMenuContent,
    NavigationMenuLink,
};
